import * as React from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import { Provider, ProviderContext } from '../shared-exports';
import { RouteContext } from './route';
import { UserContext, UpdateCartAction } from './user';
import { DataContext } from './modelCache';
import { ProductLocationModal } from '../components/ui/selectProductLocationModal';
import { EditorFooter } from '../components/product/EditorFooter'
import { InfastningProduct } from '../models/infastningProduct';
import { CartModalContext } from './editormodels/cart-modal';
import { ModelHttpService } from '../services';

interface Interface {
	/** The selected view of the edited product between platt/3d/knäck */
	viewMode: "flat" | "3d" | "butterscotch"
	/** The selected categories in nested order */
	categories: Array<ICategory>
	/** Returns the current order item being edited, it might not exist in the users cart */
	current: () => IOrderItem | undefined

	/** Returns if the current edited order item is new or if the user is editing an existing item */
	isNew: () => boolean
	/** Adjusts the current product being edited, if an update object is provided it is merged with the current product */
	adjust: (update?: Partial<ISheetProduct>) => Promise<void>
	/** Sets the quantity of the the order item being edited */
	quantity: (quantity: number) => Promise<void>
	/** Selectes an existing order item for editing */
	edit: (orderItem: IOrderItem) => Promise<void>
	/** Adds a new order item for editing from a product template */
	add: (template: ISheetProductTemplate) => Promise<void>
	/** Adds a new order item for editing from a water drainage product */
	addWaterDrainage: (product: IWaterDrainageProduct[]) => Promise<void>
	/** Adds a new order item for editing from a infasting product */
	addInfastingProduct: (product: IInfastningProduct[]) => Promise<void>
	/** Adds a new order item for editing from a ovrigt product */
	addOvrigtProduct: (product: IOvrightProduct[]) => Promise<void>
	/** Saves the current product being edited to the users cart */
	save: () => Promise<void>
	/** Saves the current product being edited to the users cart */
	saveWaterDrainage: () => Promise<void>

	/** Saves the current product being edited to the users cart */
	saveInfastingProduct: () => Promise<void>
	saveOvrigtProduct: () => Promise<void>

	/** Discards the current product being edited and reverts it if it exists in the users cart */
	discard: () => Promise<void>
	/** Resets the edited product back to standard state */
	reset: () => Promise<void>

	addImageToProduct: (index: number, imageData: string, fileName: string) => void

	removeImageFromProduct: (index: number) => void
	footer: EditorFooter | null,

	backup: () => any,
	restoreBackup: () => any,
	loading: any,
	profileValues: {
		isNeedtoSet: boolean,
		frontVertices: any,
		backVertices: any,
		depth: number
	},
	clear: () => void,
}

// @ts-expect-error — interface mismatch
const DefaultContext: Interface = {
	viewMode: 'butterscotch',
	categories: [],
	current: null!,
	isNew: null!,
	adjust: null!,
	quantity: null!,
	add: null!,
	addWaterDrainage: null!,
	addInfastingProduct: null!,
	addOvrigtProduct: null!,
	edit: null!,
	save: null!,
	saveWaterDrainage: null!,
	saveInfastingProduct: null!,
	discard: null!,
	reset: null!,
	addImageToProduct: null!,
	removeImageFromProduct: null!,
	footer: null,
	backup: null!,
	restoreBackup: null!,
	loading: false,
	profileValues: {
		isNeedtoSet: true,
		frontVertices: [],
		backVertices: [],
		depth: 500
	}
}

/** The context that will be used by the provider */
export const EditorContext: React.Context<Interface> = React.createContext(DefaultContext);
export interface EditorContext extends ProviderContext<Interface> { };

interface Props { }
interface State {
	/** The current order item being edited, if any */
	orderItem?: IOrderItem
	/** If the order item was removed from the cart, a copy of the original item will be stored and restored upon discard */
	originalOrderItem?: IOrderItem
	/** The previous route before the editor was opened, will be navigated back to on discard */
	originalRoute?: string
}

/** Provider for selecting and editing products and order items */
export class EditorProvider extends Provider<Interface, Props, State> {
	private savedBackup: any

	private router!: RouteContext
	private user!: UserContext
	private data!: DataContext
	private cartmodal!: CartModalContext

	/** Exposes the context to use */
	protected use() { return EditorContext }

	/** Overrides base render to consume RouteContext, UserContext, DataContext and CartModalContext */
	public render() {
		const Context = this.use();
		return (
			<RouteContext.Consumer>
				{router => {
					this.router = router as RouteContext;
					return (
						<UserContext.Consumer>
							{user => {
								this.user = user as UserContext;
								return (
									<DataContext.Consumer>
										{data => {
											this.data = data as DataContext;
											return (
												<CartModalContext.Consumer>
													{cartmodal => {
														this.cartmodal = cartmodal as CartModalContext;
														return (
															<Context.Provider value={this.state}>
																{this.props.children}
															</Context.Provider>
														);
													}}
												</CartModalContext.Consumer>
											);
										}}
									</DataContext.Consumer>
								);
							}}
						</UserContext.Consumer>
					);
				}}
			</RouteContext.Consumer>
		);
	}

	/** Declares the initial state */
	protected initialState(): Interface & State {
		return {
			...DefaultContext,
			current: this.current.bind(this),
			isNew: this.isNew.bind(this),
			adjust: this.adjust.bind(this),
			quantity: this.quantity.bind(this),
			add: this.add.bind(this),
			addWaterDrainage: this.addWaterDrainage.bind(this),
			addInfastingProduct: this.addInfastingProduct.bind(this),
			addOvrigtProduct: this.addOvrigtProduct.bind(this),
			edit: this.edit.bind(this),
			save: this.save.bind(this),
			saveWaterDrainage: this.saveProductToCart.bind(this),
			saveInfastingProduct: this.saveProductToCart.bind(this),
			saveOvrigtProduct: this.saveProductToCart.bind(this),
			discard: this.discard.bind(this),
			reset: this.reset.bind(this),
			addImageToProduct: this.addImageToProduct.bind(this),
			removeImageFromProduct: this.removeImageFromProduct.bind(this),
			footer: null,

			backup: this.backup.bind(this),
			restoreBackup: this.restoreBackup.bind(this)
		}
	}

	public backup() {
		// console.log("saving backup")
		this.savedBackup = JSON.stringify(this.state.orderItem)

	}

	public restoreBackup() {
		// console.log("restoring backup")
		this.setState({ orderItem: JSON.parse(this.savedBackup) })

	}

	/** Returns the current order item being edited, it might not exist in the users cart */
	protected current() {
		return (this.state.orderItem)
	}

	/** Returns if the current edited order item is new or if the user is editing an existing item */
	protected isNew() {
		return (!this.state.originalOrderItem)
	}

	/** Adjusts the current product being edited, if an update object is provided it is merged with the current product */
	protected async adjust(update?: Partial<ISheetProduct>) {
		if (update && this.state.orderItem) {
			await this.setState({
				// @ts-expect-error — interface mismatch
				orderItem: { ...this.state.orderItem, product: { ...this.state.orderItem.product, ...update } }
			});
		}
		else {
			this.forceUpdate();
		}
		this.backup()

	}

	/** Sets the quantity of the the order item being edited */
	protected async quantity(quantity: number) {
		if (this.state.orderItem) {
			await this.setState({
				orderItem: { ...this.state.orderItem, quantity: Math.max(1, quantity) }
			});
		}
		else {
			this.forceUpdate();
		}
	}

	/** Adds a new order item for editing from a product template */
	protected async add(template: ISheetProductTemplate) {
		// TODO: Make a nicer prompt
		// if (this.state.orderItem) {
		// 	alert("Du redigerar redan en produkt!");
		// 	return;
		// }

		// Create a new order item
		const item = await this.user.createOrderItem(template, undefined, true);
		// console.log('editor templates',item)
		// console.log({ item })

		if (template.isOwnProduct) {
			item.product ? item.product.userUploads = [[], [], []] : undefined
		}
		// Set the newly created order row as the one being edited and navigate to the product edit view
		await this.setState({ orderItem: item, originalRoute: this.router.location.pathname });

		if (template.isOwnProduct) {
			if (template.openFotoComponent) {
				this.router.push('/foto', 'slide-left');
				return
			} else {
				this.router.push('/ritaproduct', 'slide-left');
				return
			}
		}
		this.router.push('/product/edit', 'slide-left');
		// console.log(template)
	}

	/** Adds a new order item for editing from a water drainage product */
	protected async addWaterDrainage(product: IWaterDrainageProduct[]) {
		const item = await this.user.createWaterDrainageOrderItem(product, 'waterDrainage');
		// Set the newly created order row as the one being edited and navigate to the product edit view
		await this.setState({ orderItem: item, originalRoute: this.router.location.pathname });
		// this.router.push('/product/edit', 'slide-left');
	}

	/** Adds a new order item for editing from a Infasting product */
	protected async addInfastingProduct(product: InfastningProduct[]) {
		const item = await this.user.createInfastningOrderItem(product, 'infästning');
		// Set the newly created order row as the one being edited and navigate to the product edit view
		await this.setState({ orderItem: item, originalRoute: this.router.location.pathname });
		// this.router.push('/product/edit', 'slide-left');
	}

	/** Adds a new order item for editing from a ovrigt product */
	protected async addOvrigtProduct(product: IOvrightProduct[]) {
		const item = await this.user.createOvrigtOrderItem(product, 'övrigt');
		// Set the newly created order row as the one being edited and navigate to the product edit view
		await this.setState({ orderItem: item, originalRoute: this.router.location.pathname });
		// this.router.push('/product/edit', 'slide-left');
	}

	/** Selectes an existing order item for editing */
	protected async edit(orderItem: IOrderItem) {
		// Verify the current state
		// TODO: make prompt nicer
		// if (this.state.orderItem) {
		// 	alert("Du redigerar redan en produkt");
		// 	return;
		// }

		// if (orderItem.product && orderItem.product.isOwnProduct) {
		// 	let order: any = await new ModelHttpService().findUserUploads<IOrderItem>(orderItem._id);
		// 	console.log('order', order);
		// 	orderItem.product.userUploads = order.product.userUploads
		// }

		// Store the current order item as the original
		const originalOrderItem = _cloneDeep(orderItem);
		// Remove the product from the cart
		// await this.user.updateCart(UpdateCartAction.RemoveProduct, orderItem.product);

		// Set it as the current item being edited
		await this.setState({ orderItem, originalOrderItem, originalRoute: this.router.location.pathname });
		if (orderItem.product && orderItem.product.isOwnProduct) {
			this.router.push('/ritaproduct', 'slide-right')
			return
		}
		if (orderItem.product && orderItem.product.isFotoProduct) {
			this.router.push('/foto', 'slide-right')
			return
		}
		if (orderItem.type && orderItem.type === "waterDrainage") {
			await this.data.updateState({ selectedCategory: 'Vattenavrinning' });
			this.router.push("/product/gallery/category", "slide-right");
			return
		} else {
			// Go to the edit view
			this.router.push('/product/edit', 'slide-right');
		}

	}

	/** Saves the current product being edited to the users cart */
	protected async save() {
		this.setState({ loading: true })
		const item = this.state.orderItem;
		// Verify the current state
		if (!item) {
			console.error("[Editor Context] tried to save a non existing item");
			return;
		}

		// const fetchAsBlob = async (url: string) => await fetch(url)
		// .then(response => response.blob());

		// const convertBlobToBase64 = (blob: any) : Promise<string>  =>  {
		// 	return new Promise((resolve, reject) => {
		// 		const reader = new FileReader;
		// 		reader.onerror = reject;
		// 		reader.onload = () => {
		// 			resolve(reader.result ? reader.result.toString() : "");
		// 		};
		// 		reader.readAsDataURL(blob);
		// 	})
		// };

		// Callback method
		const whenRedy = async () => {
			if (item.product) {

				// debugger
				// if (item.product.isOwnProductImageDataURL) {
				// 	for(let i=0; i< item.product.userUploads.length; i++) {
				// 		if (item.product.userUploads[i].length > 1) {
				// 			let tempBlob = await fetchAsBlob(item.product.userUploads[i][1]);
				// 			item.product.userUploads[i][1] = await convertBlobToBase64(tempBlob);
				// 		}
				// 	}
				// }	
				// Add the entire order item to the user cart

				// @ts-expect-error — interface mismatch
				await this.user.updateCart(UpdateCartAction.AddProduct, item.product, item);
				if (this.user.currentProject) {
					await this.router.push('/project/cart', 'slide-right');
				} else {
					await this.router.push('/project/unsorted', 'slide-right');
				}

				// Stop editing and navigate to the correct cart view
				await this.setState({ orderItem: undefined, originalRoute: undefined, originalOrderItem: undefined });
				this.setState({ loading: false })
			}
		}

		let needToAsk = false;
		if (this.user.currentProject) {
			if (this.user.currentProject.selectedBuilding) {
				if (!this.user.currentProject.selectedBuilding.facades.length) {
					needToAsk = true;
				}
				if (!this.user.currentProject.selectedFloorOrRoof) {
					needToAsk = true;
				}
				if (!this.user.currentProject.selectedFloorOrRoof && this.user.currentProject.selectedOtherBuilding) {
					needToAsk = false;
				}
				if (!this.user.currentProject.selectedFloorOrRoof && !this.user.currentProject.selectedOtherBuilding) {
					needToAsk = true;
				}
			} else {
				needToAsk = true;
			}
		}
		// NO PROJECT SELECTED - Ask the user to select a location if none exists, then do the callback method
		if (!this.user.currentProject || needToAsk) {
			this.setState({ loading: false })
			await this.cartmodal.show(ProductLocationModal(this.data.availableProjects(), this.data, async (result) => {
				if (result.saveInUnsortedProducts != undefined) {
					if (result.floor) {
						let getOrderData = [];
						for (let i = 0; i < result.floor.cart.length; i++) {
							if (typeof result.floor.cart[i] === "string") {
								getOrderData.push(result.floor.cart[i]);
							}
						}
						if (getOrderData && getOrderData.length) {
							let cartData: any = await new ModelHttpService().getOrdersdata({ ids: getOrderData });
							if (cartData && cartData.length) {
								result.floor.cart = cartData;
							}
						}
						await this.user.updateState({
							currentProject: { ...result.project!, selectedBuilding: result.building!, selectedFacade: result.facade!, selectedFloorOrRoof: result.floor! },
						})
					}
					if (result.other) {
						let getOrderData = [];
						for (let i = 0; i < result.other.cart.length; i++) {
							if (typeof result.other.cart[i] === "string") {
								getOrderData.push(result.other.cart[i]);
							}
						}
						if (getOrderData && getOrderData.length) {
							let cartData: any = await new ModelHttpService().getOrdersdata({ ids: getOrderData });
							if (cartData && cartData.length) {
								result.other.cart = cartData;
							}
						}
						await this.user.updateState({
							currentProject: { ...result.project!, selectedOtherBuilding: result.other, selectedBuilding: undefined, selectedFacade: undefined, selectedFloorOrRoof: undefined },
						})
					}
					await whenRedy();
				}
				await this.cartmodal.hide();
			}, true, () => { this.cartmodal.hide(); }));
		}
		// ALDREADY KNOW WHERE TO SAVE
		else {
			await whenRedy();
		}
	}

	// Callback method
	whenRedy = async (items: any) => {
		for (let item of items) {
			// @ts-expect-error — interface mismatch
			const price = item && item.product && item.product.additionalPrice ? item.product.basePrice + (Object.values(item.product.additionalPrice).reduce((a, b) => a + b, 0)) : item;
			item && item.product ? item.product.basePrice = price : item
			// Add the entire order item to the user cart
			await this.user.updateCart(UpdateCartAction.AddProduct, item.product, item);

			// Stop editing and navigate to the correct cart view
			await this.setState({ orderItem: undefined, originalRoute: undefined, originalOrderItem: undefined });
		}
		if (this.user.currentProject) {
			await this.router.push('/project/cart', 'slide-right');
		} else {
			await this.router.push('/project/unsorted', 'slide-right');
		}
		this.setState({ loading: false })
	}

	/** Saves the current product being edited to the users cart */
	protected async saveProductToCart() {
		this.setState({ loading: true })
		const item = this.state.orderItem;
		// Verify the current state
		if (!item) {
			console.error("[Editor Context] tried to save a non existing item");
			return;
		}

		// NO PROJECT SELECTED - Ask the user to select a location if none exists, then do the callback method
		if (!this.user.currentProject) {
			this.setState({ loading: false })
			await this.cartmodal.show(ProductLocationModal(this.data.availableProjects(), this.data, async (result) => {
				if (result.saveInUnsortedProducts != undefined) {
					if (result.floor) {
						await this.user.updateState({
							currentProject: { ...result.project!, selectedBuilding: result.building!, selectedFacade: result.facade!, selectedFloorOrRoof: result.floor! },
						})
					}
					if (result.other) {
						await this.user.updateState({
							currentProject: { ...result.project!, selectedOtherBuilding: result.other, selectedBuilding: undefined, selectedFacade: undefined, selectedFloorOrRoof: undefined },
						})
					}
					await this.whenRedy(item);
				}
				await this.cartmodal.hide();
			}));
		}
		// ALDREADY KNOW WHERE TO SAVE
		else {
			await this.whenRedy(item);
		}
	}

	private addImageToProduct(index: number, imageData: string, fileName: string) {
		const orderItem = this.state.orderItem
		if (orderItem == undefined) {
			console.error("Cannot add image to orderItem. Reason: OrderItem is undefined")
			return
		}
		orderItem.product ? orderItem.product.userUploads[index] = [fileName, imageData] : undefined
		orderItem.product ? orderItem.product.isOwnProductImageDataURL = true : null
		this.setState({
			orderItem
		})
		this.backup()
	}

	private removeImageFromProduct(index: number) {
		const orderItem = this.state.orderItem
		if (orderItem == undefined) {
			console.error("Cannot remove image from orderItem. Reason: OrderItem is undefined")
			return
		}

		orderItem.product ? orderItem.product.userUploads[index] = [] : undefined
		this.setState({
			orderItem
		})
		this.backup()

	}

	/** Gets or sets the selected view mode of the edited product */
	protected viewMode(viewMode?: Interface['viewMode']) {
		if (viewMode !== undefined) {
			this.updateState({ viewMode });
			return viewMode;
		}
		return this.state.viewMode;
	}

	/** Discards the current product being edited and reverts it if it exists in the users cart */
	protected async discard() {
		if (this.state.originalRoute) {
			await this.router.replace(this.state.originalRoute, 'none');
		}
		if (this.state.originalOrderItem) {
			// @ts-expect-error — interface mismatch
			await this.user.updateCart(UpdateCartAction.AddProduct, this.state.originalOrderItem.product, this.state.originalOrderItem);
		}
		this.state.profileValues.backVertices = [];
		this.state.profileValues.frontVertices = [];
		this.state.profileValues.depth = 0;
		await this.setState({ orderItem: undefined, originalRoute: undefined, originalOrderItem: undefined });
	}

	/** Resets the current edited product to the state it was before editing began */
	protected async reset() {
		if (!this.state.orderItem) { return; }

		// Reset to the state before editing began
		if (this.state.originalOrderItem) {
			await this.setState({ orderItem: _cloneDeep(this.state.originalOrderItem) });
		}
		// Reset to a new a brand new product
		else {
			const template = await this.data.findTemplate(this.state.orderItem && this.state.orderItem.product && this.state.orderItem.product.template);
			if (template) {
				this.state.orderItem.product = this.data.createProduct(template)
				await this.setState({ orderItem: this.state.orderItem });
			}
		}
	}

	/** Updates the state of the provider */
	protected async updateState(state: Partial<Interface> | Interface) {
		await this.setState(state as Interface);
	}
}
